import { useState, useEffect } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";
import { minutes } from "@/utils";

interface Asset {
  id: string;
  name: string;
  coingecko_id: number;
  coinpaprika_id: number;
  fiat: boolean;
  crypto: boolean;
  nft: boolean;
  contract_address: string;
  exchange: any;
  icon_url: string;
  metadata: string;
}

interface PaginatedResponse<T> {
  data: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

interface UseGetAssetsProps {
  initialSearchQuery?: string;
  cursor?: string | null;
  limit?: number;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
}

export const useAssetQueryKey = ["assets"] as const;

export const useGetAssets = ({
  initialSearchQuery,
  cursor = null,
  limit = 20,
  refetchOnWindowFocus = true,
  staleTime = 1 * minutes,
}: UseGetAssetsProps = {}) => {
  const { user, getToken } = useAuth();
  const email = user?.email;

  const [searchQuery, setSearchQuery] = useState(initialSearchQuery ?? "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Debounce searchQuery by 300ms
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const getAssets = async (): Promise<PaginatedResponse<Asset> | Asset[]> => {
    try {
      const token = await getToken();
      const res = await authorizedAxios(token).get(
        debouncedSearchQuery
          ? `/asset?symbol=${debouncedSearchQuery}`
          : "/asset"
      );
      return res.data;
    } catch (error) {
      console.log("[useGetAssets] error", error);
      throw error;
    }
  };

  const queryKey = [...useAssetQueryKey, debouncedSearchQuery];

  const {
    isLoading: isQueryLoading,
    isIdle,
    isSuccess,
    error,
    isError,
    data,
    refetch,
  }: UseQueryResult<PaginatedResponse<Asset> | Asset[], unknown> = useQuery({
    queryKey,
    queryFn: getAssets,
    enabled: !!email,
    refetchOnWindowFocus,
    staleTime,
    keepPreviousData: true,
  });

  const assets = Array.isArray(data) ? data : data?.data ?? [];
  const nextCursor = Array.isArray(data) ? null : data?.nextCursor;
  const hasMore = Array.isArray(data) ? false : data?.hasMore ?? false;

  return {
    isQueryLoading,
    isIdle,
    error,
    assets,
    nextCursor,
    hasMore,
    refetch,
    searchQuery,
    setSearchQuery,
  };
};
